import React from 'react';
import { Star, Quote, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

// Google Review Link - Direct to Empire Lawn Care reviews
const GOOGLE_REVIEW_LINK = "https://www.google.com/search?q=empire+lawn+care+and+maintenance+coffs+harbour+reviews#lrd=0xa670ee9d446ea23b:0x5cd4f2f0a951cb75,3";

const testimonials = [
  {
    id: 1,
    name: 'Sarah M.',
    location: 'Coffs Harbour',
    rating: 5,
    text: 'Absolutely fantastic service! Jacob and his team transformed my overgrown lawn into a beautiful, manicured garden. Very professional and reasonably priced. Highly recommend!',
    service: 'Lawn Care',
  },
  {
    id: 2,
    name: 'David K.',
    location: 'Sawtell',
    rating: 5,
    text: 'Ryan did an amazing job pressure cleaning our driveway and paths. The difference is incredible - looks brand new! Will definitely be using Empire again.',
    service: 'Pressure Cleaning',
  },
  {
    id: 3,
    name: 'Michelle T.',
    location: 'Korora',
    rating: 5,
    text: 'Had my solar panels cleaned and the difference in efficiency is noticeable. Great communication, on time, and very thorough. Five stars!',
    service: 'Solar Panel Clean',
  },
  {
    id: 4,
    name: 'Peter & Jenny H.',
    location: 'Woolgoolga',
    rating: 5,
    text: 'We use Empire for our complete home exterior package. The boys are always reliable, friendly, and do exceptional work. Our property has never looked better!',
    service: 'Complete Package',
  },
  {
    id: 5,
    name: 'Andrew B.',
    location: 'Coffs Harbour',
    rating: 5,
    text: 'Called for an urgent gutter clean before storm season. They fit me in quickly and did a thorough job. Great local business with excellent customer service.',
    service: 'Gutter Clean',
  },
  {
    id: 6,
    name: 'Lisa R.',
    location: 'Moonee Beach',
    rating: 5,
    text: 'The roof cleaning service was outstanding. They removed years of built-up grime and moss. Very professional team and the quote was exactly as promised.',
    service: 'Roof Clean',
  },
];

export default function Testimonials() {
  return (
    <section id="testimonials" className="py-20 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className={i < 4 ? "text-amber-500 fill-amber-500" : "text-amber-500 fill-amber-500/70"} 
                  size={24} 
                />
              ))}
            </div>
            <span className="font-oswald text-2xl font-bold text-green-900 ml-2">4.7</span>
          </div>
          <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-green-900 mb-4">
            What Our Customers Say
          </h2>
          <div className="gold-accent-line w-24 mx-auto mb-6" />
          <p className="font-inter text-gray-600 max-w-2xl mx-auto">
            Don't just take our word for it - hear from our satisfied customers across the Coffs Harbour region.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="bg-slate-50 rounded-xl p-6 border border-gray-100 relative animate-fade-in-up"
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`testimonial-${testimonial.id}`}
            >
              {/* Quote Icon */}
              <Quote className="absolute top-4 right-4 text-green-900/10" size={40} />
              
              {/* Stars */}
              <div className="flex mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="text-amber-500 fill-amber-500" size={18} />
                ))}
              </div>

              {/* Review Text */}
              <p className="font-inter text-gray-700 text-sm leading-relaxed mb-4">
                "{testimonial.text}"
              </p>

              {/* Reviewer Info */}
              <div className="border-t border-gray-200 pt-4">
                <p className="font-oswald font-semibold text-gray-900">
                  {testimonial.name}
                </p>
                <p className="font-inter text-sm text-gray-500">
                  {testimonial.location}
                </p>
                <span className="inline-block mt-2 px-3 py-1 bg-green-900/10 text-green-900 text-xs font-medium rounded-full">
                  {testimonial.service}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Google Review Section */}
        <div className="mt-12 flex flex-col items-center gap-6">
          {/* Google Review Badge */}
          <div className="inline-flex items-center bg-white border border-gray-200 rounded-lg px-6 py-4 shadow-sm">
            <svg className="w-8 h-8 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <div className="text-left">
              <p className="font-inter text-sm font-medium text-gray-900">
                Rated 4.7 on Google
              </p>
              <p className="font-inter text-xs text-gray-500">
                Based on customer reviews
              </p>
            </div>
          </div>

          {/* Review Us Button */}
          <a
            href={GOOGLE_REVIEW_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-900 hover:bg-green-800 text-white font-oswald font-medium px-6 py-3 rounded-lg transition-colors btn-empire"
            data-testid="google-review-btn"
          >
            <Star className="fill-amber-500 text-amber-500" size={20} />
            Leave Us a Google Review
            <ExternalLink size={16} />
          </a>
          <p className="font-inter text-sm text-gray-500">
            Your feedback helps us serve you better!
          </p>
        </div>
      </div>
    </section>
  );
}
